import { CATEGORIAS_GARANTIA, id } from "../domain/model";
import type { Garantia } from "../domain/types";
import { Campo, Numero } from "./ui";
import { usePedirTexto } from "./Dialogos";
import Icono from "./Icono";

// Lista editable de garantías (categoría, años y notas). La usan la lista
// general del paso 3 y las de cada sitio y pieza del paso 2.
// - conCheck: la general trae categorías fijas y el vendedor marca cuáles
//   ofrece. En sitio y pieza cada categoría se agrega a propósito, así que
//   se ofrece en cuanto tiene años (sin check).
// - sugeridas: atajos para agregar las categorías de siempre que falten.
export default function ListaGarantias({
  lista,
  onChange,
  conCheck = false,
  sugeridas = false,
  textoAgregar = "Agregar categoría",
}: {
  lista: Garantia[];
  onChange: (lista: Garantia[]) => void;
  conCheck?: boolean;
  sugeridas?: boolean;
  textoAgregar?: string;
}) {
  const pedirTexto = usePedirTexto();
  const agregar = (categoria: string, anios: number | null) =>
    onChange([
      ...lista,
      {
        id: id(),
        categoria,
        anios,
        notas: "",
        incluida: !conCheck && anios != null && anios > 0,
      },
    ]);
  const faltan = sugeridas
    ? CATEGORIAS_GARANTIA.filter(
        ([nombre]) => !lista.some((g) => g.categoria === nombre),
      )
    : [];
  return (
    <>
      {lista.map((g) => {
        // Una garantía sin años no se puede ofrecer: el check se habilita
        // al escribirlos y se desmarca solo si se borran.
        const sinAnios = g.anios == null || g.anios <= 0;
        const editar = (cambio: Partial<Garantia>) =>
          onChange(lista.map((x) => (x.id === g.id ? { ...x, ...cambio } : x)));
        return (
          <fieldset
            className="warranty"
            key={g.id}
            data-excluida={!g.incluida || undefined}
          >
            <legend className="sr-only">{g.categoria}</legend>
            <header className="row between">
              {conCheck ? (
                <label className="check">
                  <input
                    type="checkbox"
                    checked={g.incluida}
                    disabled={sinAnios && !g.incluida}
                    aria-describedby={
                      sinAnios ? `${g.id}-requisito` : undefined
                    }
                    onChange={(e) => editar({ incluida: e.target.checked })}
                  />
                  <strong>{g.categoria}</strong>
                </label>
              ) : (
                <strong>{g.categoria}</strong>
              )}
              {sinAnios && !g.incluida ? (
                <small id={`${g.id}-requisito`} className="requisito">
                  {conCheck
                    ? "Escribe los años para poder incluirla"
                    : "Escribe los años para que salga en el PDF"}
                </small>
              ) : null}
              <button
                type="button"
                className="icon-button danger"
                aria-label={`Quitar garantía ${g.categoria}`}
                title="Quitar garantía"
                onClick={() => onChange(lista.filter((x) => x.id !== g.id))}
              >
                <Icono nombre="eliminar" />
              </button>
            </header>
            <div className="grid warranty-campos">
              <Numero
                label="Años *"
                value={g.anios}
                min={1}
                onChange={(n) =>
                  editar(
                    n == null || n <= 0
                      ? { anios: n, incluida: false }
                      : conCheck
                        ? { anios: n }
                        : { anios: n, incluida: true },
                  )
                }
              />
              <Campo label="Notas (opcional)">
                <input
                  value={g.notas}
                  onChange={(e) => editar({ notas: e.target.value })}
                />
              </Campo>
            </div>
          </fieldset>
        );
      })}
      {faltan.length ? (
        <div className="row garantias-agregar">
          {faltan.map(([nombre, anios]) => (
            <button
              type="button"
              key={nombre}
              onClick={() => agregar(nombre, anios)}
            >
              <Icono nombre="agregar" /> {nombre}
            </button>
          ))}
          {botonOtra("Otra…")}
        </div>
      ) : (
        botonOtra(textoAgregar, true)
      )}
    </>
  );

  function botonOtra(texto: string, bloque = false) {
    return (
      <button
        type="button"
        className={bloque ? "agregar-bloque" : undefined}
        onClick={async () => {
          const nombre = await pedirTexto({
            titulo: "Nueva categoría de garantía",
            etiqueta: "Nombre de la categoría",
            confirmar: "Agregar categoría",
          });
          if (nombre) agregar(nombre, null);
        }}
      >
        <Icono nombre="agregar" /> {texto}
      </button>
    );
  }
}
